import { createContext, type ReactNode, useContext, useMemo } from 'react';

import { getAccessToken } from '../auth/driver-auth';
import { useDriverAuth } from '../auth/driver-auth-provider';
import { DriverErpService } from './driver-erp';

// F007: at most one automatic session-recovery per window (mirrors the
// Portal ErpProvider's throttle, portal-erp-provider.tsx) — several pickups
// screens can 401 within milliseconds of each other on the same dead token,
// and a server that 401s even a FRESH token (clock skew, misconfig) must
// never turn that into a redirect-loop.
let lastRecovery = 0;
const RECOVERY_WINDOW_MS = 30_000;

const DriverErpContext = createContext<DriverErpService | null>(null);

/**
 * Provides a single {@link DriverErpService} bound to the PKCE token store so
 * the My Pickups / Pickup detail screen containers (Tasks 11-12) can resolve it
 * via {@link useDriverErp}.
 *
 * F007: wires the service's `onAuthExpired` 401 hook to
 * {@link useDriverAuth}'s `recoverExpiredSession` so an expired/rejected
 * access token recovers the session (a silent native refresh, or a fresh PKCE
 * login) instead of every screen rendering the same generic "failed to load"
 * a network blip would show.
 */
export function DriverErpProvider({ children }: { children: ReactNode }) {
  const { recoverExpiredSession } = useDriverAuth();
  const service = useMemo(
    () =>
      new DriverErpService(getAccessToken, () => {
        const now = Date.now();
        if (now - lastRecovery > RECOVERY_WINDOW_MS) {
          lastRecovery = now;
          recoverExpiredSession();
        }
      }),
    [recoverExpiredSession]
  );
  return (
    <DriverErpContext.Provider value={service}>
      {children}
    </DriverErpContext.Provider>
  );
}

export function useDriverErp(): DriverErpService {
  const service = useContext(DriverErpContext);
  if (!service) {
    throw new Error('useDriverErp must be used within a DriverErpProvider');
  }
  return service;
}
